/**
 * World-renderer lifecycle — the single entry point the React shell invokes
 * when the world channel mounts.
 *
 * Responsibilities:
 *   - Build the PixiJS Application + layer hierarchy (`createSceneGraph`).
 *   - Wire input-driven camera state (`createCameraController`).
 *   - Instantiate the per-layer rendering subsystems (tiles, walls, props)
 *     that subscribe to the world model.
 *   - Drive a 60 fps ticker callback that:
 *       1. Applies the camera transform to the world container.
 *       2. Reads the current `WorldModel` from `useWorldModel` and hands
 *          it to each subsystem's `render(model, cam, w, h)`.
 *
 * The ticker reads from the zustand store out-of-React, so model updates
 * don't trigger React re-renders of the world pane.
 */

import { Graphics, type Application } from 'pixi.js';

import { deriveInspection, hitTestTile, hitTestTileAll, useInspection } from '../inspect';
import { routeTileClick } from '../input/movement';
import { phase0, debugLog } from '../log';
import { useLiveWire } from '../wire';
import { screenToWorld, tileToIso, TILE_HEIGHT_PX, TILE_WIDTH_PX } from './iso';
import { useWorldModel, type WorldModel } from '../world';
import { createAvatarRenderer, type AvatarRenderer } from './avatars';
import { createBubbleRenderer, type BubbleRenderer } from './bubbles';
import { createCameraController, type CameraController } from './camera';
import { createCaveTileRenderer, createCaveMouthRenderer, type CaveTileRenderer, type CaveMouthRenderer } from './caves';
import { createDayNightTinter, type DayNightTinter } from './daynight';
import { createFogRenderer, type FogRenderer } from './fog';
import { createSceneGraph, type SceneGraph } from './scene';
import { createPropRenderer, type PropRenderer } from './props';
import { createCliffEdgeRenderer, type CliffEdgeRenderer } from './cliff-edges';
import { createTileRenderer, type TileRenderer } from './tiles';
import { createWallRenderer, type WallRenderer } from './walls';
import { createWeatherRenderer, type WeatherRenderer } from './weather';
import { createDevOverlayRenderer, type DevOverlayRenderer } from './dev-overlay';
import { createMoistureOverlayRenderer, type MoistureOverlayRenderer } from './moisture';
import { useContextMenu, type MenuItem } from '../context-menu';
import { propLabel } from '../data/prop-catalog';
import { materialLabel } from '../data/labels';

/** GH#8 D3 — green diamond outline that fades over ~250ms on the
 *  target tile, acknowledging a successful click-to-move dispatch. */
const MOVE_FLASH_MS = 250;
const MOVE_FLASH_COLOR = 0xb6f562;

/** Destination marker for active MoveTo navigation — light-blue pulsing
 *  diamond on the goal tile. Alpha oscillates ~1 Hz between 0.3 and 0.7. */
const NAV_MARKER_COLOR = 0x44aaff;
const NAV_MARKER_ALPHA_MIN = 0.3;
const NAV_MARKER_ALPHA_MAX = 0.7;
const NAV_MARKER_PULSE_HZ = 1;

export interface WorldRendererHandle {
  app: Application;
  scene: SceneGraph;
  camera: CameraController;
  /** Idempotent teardown. Safe to call from React StrictMode's unmount leg. */
  destroy: () => void;
}

export interface WorldRendererOptions {
  background?: string;
  antialias?: boolean;
  initialZoomIndex?: number;
}

export async function mountWorldRenderer(
  host: HTMLElement,
  options: WorldRendererOptions = {},
): Promise<WorldRendererHandle> {
  const scene = await createSceneGraph(host, {
    background: options.background,
    antialias: options.antialias,
  });

  // Expose the canvas to keyboard focus so arrow-key panning works without
  // clicking into the element first.
  scene.app.canvas.tabIndex = 0;
  scene.app.canvas.oncontextmenu = (e) => e.preventDefault();

  const camera = createCameraController({
    target: scene.app.canvas,
    initialZoomIndex: options.initialZoomIndex,
  });

  // Layer renderers — each subscribes to the world model via `getState()`.
  const tiles: TileRenderer = createTileRenderer(scene.layers);
  const cliffEdges: CliffEdgeRenderer = createCliffEdgeRenderer(scene.layers);
  const walls: WallRenderer = createWallRenderer(scene.layers);
  const props: PropRenderer = createPropRenderer(scene.layers);
  const avatars: AvatarRenderer = createAvatarRenderer(scene.layers);
  const weather: WeatherRenderer = createWeatherRenderer(scene.layers);
  const tinter: DayNightTinter = createDayNightTinter(scene.worldContainer);
  const bubbles: BubbleRenderer = createBubbleRenderer(scene.layers);
  const fog: FogRenderer = createFogRenderer(scene.layers);
  const caveTiles: CaveTileRenderer = createCaveTileRenderer(scene.layers);
  const caveMouths: CaveMouthRenderer = createCaveMouthRenderer(scene.layers);
  const devOverlay: DevOverlayRenderer = createDevOverlayRenderer(scene.layers);
  const moisture: MoistureOverlayRenderer = createMoistureOverlayRenderer(scene.layers);

  // Trailing-edge debounce for MoveTo: rapid clicks accumulate and
  // only the last destination gets sent, preventing the avatar from
  // bouncing between intermediate targets.
  let moveToDebounce: number | null = null;
  const MOVE_TO_DEBOUNCE_MS = 150;

  const onPointerDown = (e: PointerEvent): void => {
    if (e.button === 0) {
      useContextMenu.getState().close();
    }

    if (e.button === 2) {
      const rect = scene.app.canvas.getBoundingClientRect();
      const sx = e.clientX - rect.left;
      const sy = e.clientY - rect.top;
      const { x, y } = screenToWorld(
        sx,
        sy,
        camera.state,
        scene.app.screen.width,
        scene.app.screen.height,
      );
      const tileX = Math.floor(x);
      const tileY = Math.floor(y);

      const wm = useWorldModel.getState();
      const targets = hitTestTileAll(wm.model, tileX, tileY);
      const menuItems: MenuItem[] = [];

      for (const target of targets) {
        let label = '?';
        let entityType: MenuItem['entityType'] = 'terrain';

        if ('Player' in target) {
          const player = wm.model.players.get(target.Player);
          label = player?.display_name ?? `player-${target.Player}`;
          entityType = 'avatar';
        } else if ('Prop' in target) {
          const prop = wm.model.props.get(target.Prop);
          label = prop ? propLabel(prop.kind) : `Prop #${target.Prop}`;
          entityType = 'prop';
        } else if ('Building' in target) {
          const room = wm.model.rooms.get(target.Building);
          label = room ? room.archetype : `room-${target.Building}`;
          entityType = 'building';
        } else if ('Tile' in target) {
          const [tx, ty] = target.Tile;
          if (tx >= 0 && tx < wm.model.width && ty >= 0 && ty < wm.model.height) {
            const idx = ty * wm.model.width + tx;
            label = materialLabel(wm.model.tileMaterials[idx] ?? 0);
          } else {
            label = `Tile (${tx}, ${ty})`;
          }
          entityType = 'terrain';
        }

        menuItems.push({ kind: 'inspect', label, entityType, target });
      }

      const selfPlayer =
        wm.selfPlayerId !== null ? wm.model.players.get(wm.selfPlayerId) : null;
      const onSelfTile =
        selfPlayer !== null &&
        selfPlayer !== undefined &&
        selfPlayer.position[0] === tileX &&
        selfPlayer.position[1] === tileY;
      if (!onSelfTile) {
        const walkTarget = findWalkTarget(
          wm.model,
          tileX,
          tileY,
          selfPlayer?.position ?? null,
        );
        menuItems.push({
          kind: 'walk',
          label: 'Walk Here',
          entityType: 'terrain',
          target: { Tile: walkTarget },
        });
      }

      if (hasWaterNearby(wm.model, tileX, tileY)) {
        menuItems.push({
          kind: 'fish',
          label: 'Fish',
          entityType: 'terrain',
          target: { Tile: [tileX, tileY] },
        });
      }

      useContextMenu.getState().show(e.clientX, e.clientY, tileX, tileY, menuItems);
      return;
    }

    if (e.button !== 0) return;

    const rect = scene.app.canvas.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
    const { x, y } = screenToWorld(
      sx,
      sy,
      camera.state,
      scene.app.screen.width,
      scene.app.screen.height,
    );
    const tileX = Math.floor(x);
    const tileY = Math.floor(y);

    const wm = useWorldModel.getState();
    const selfPlayer =
      wm.selfPlayerId !== null ? wm.model.players.get(wm.selfPlayerId) : null;
    const action = routeTileClick({
      selfTile: selfPlayer ? selfPlayer.position : null,
      clickTile: [tileX, tileY],
      shiftKey: e.shiftKey,
    });

    if (action.kind === 'move') {
      const sent = useLiveWire.getState().send({
        plane: 'World',
        intent: { Move: { direction: action.direction } },
      });
      phase0.info('move_intent_dispatch', {
        direction: action.direction,
        tile_x: tileX,
        tile_y: tileY,
        sent,
      });
      debugLog('input', 'info', `move direction=${action.direction} tile=(${tileX},${tileY}) sent=${sent}`);
      if (sent) flashTargetTile(scene, tileX, tileY);
      return;
    }

    if (action.kind === 'moveTo') {
      if (moveToDebounce !== null) window.clearTimeout(moveToDebounce);
      const tx = tileX;
      const ty = tileY;
      moveToDebounce = window.setTimeout(() => {
        moveToDebounce = null;
        const sent = useLiveWire.getState().send({
          plane: 'World',
          intent: { MoveTo: { target: [tx, ty] } },
        });
        phase0.info('move_to_intent_dispatch', { tile_x: tx, tile_y: ty, sent });
        debugLog('input', 'info', `moveTo target=(${tx},${ty}) sent=${sent}`);
        if (sent) flashTargetTile(scene, tx, ty);
      }, MOVE_TO_DEBOUNCE_MS);
      return;
    }

    // action.kind === 'inspect'
    const target = hitTestTile(wm.model, tileX, tileY);
    const data = deriveInspection(wm.model, target);
    if (data) {
      useInspection.getState().openWith(target, data);
      const kind = 'Tile' in target ? 'Tile' : 'Prop' in target ? 'Prop' : 'Building' in target ? 'Building' : 'Player' in target ? 'Player' : 'Soma';
      phase0.info('inspect_request', { tile_x: tileX, tile_y: tileY, kind });
      debugLog('input', 'info', `inspect tile=(${tileX},${tileY}) kind=${kind}`);
    }
  };
  scene.app.canvas.addEventListener('pointerdown', onPointerDown);

  /** GH#8 follow-up: one-shot camera centering on the local player as
   *  soon as the model carries a known position for them. Without this,
   *  the camera defaults to world (0, 0) but the spawn lands at the
   *  arrival-zone centre (often hundreds of tiles away on a 512×512
   *  world), so the operator never sees the avatar and click-to-move
   *  has nothing to aim at. Per CLIENT_DESIGN §5 we still don't follow
   *  the avatar after this initial centring — the operator pans freely
   *  thereafter. */
  let centeredOnSelf = false;

  // ─── Destination marker state ────────────────────────────────────────────
  // Tracks the last-known navGoal for the local player so the ticker can
  // detect changes and update the marker Graphics without creating/destroying
  // a Graphics object every frame.
  let navMarkerGraphics: Graphics | null = null;
  let navMarkerGoal: [number, number] | null = null;

  function updateNavMarker(goal: [number, number] | null): void {
    if (goal === null) {
      // Clear marker.
      if (navMarkerGraphics !== null) {
        navMarkerGraphics.destroy();
        navMarkerGraphics = null;
      }
      navMarkerGoal = null;
      return;
    }
    // Check if goal changed — create or recreate the Graphics at the new tile.
    if (
      navMarkerGraphics === null ||
      navMarkerGoal === null ||
      navMarkerGoal[0] !== goal[0] ||
      navMarkerGoal[1] !== goal[1]
    ) {
      if (navMarkerGraphics !== null) {
        navMarkerGraphics.destroy();
      }
      const halfW = TILE_WIDTH_PX / 2;
      const halfH = TILE_HEIGHT_PX / 2;
      const { x: ix, y: iy } = tileToIso(goal[0], goal[1]);
      const g = new Graphics();
      g.poly([ix, iy - halfH, ix + halfW, iy, ix, iy + halfH, ix - halfW, iy]);
      g.stroke({ color: NAV_MARKER_COLOR, width: 3, alpha: 0.9 });
      g.fill({ color: NAV_MARKER_COLOR, alpha: 0.18 });
      scene.layers.floorOverlayLayer.addChild(g);
      navMarkerGraphics = g;
      navMarkerGoal = [goal[0], goal[1]];
    }
  }

  const tickerFn = (): void => {
    const viewW = scene.app.screen.width;
    const viewH = scene.app.screen.height;

    const state = useWorldModel.getState();
    const { model, selfPlayerId, tickHz, snapshotApplied } = state;
    const now = performance.now();

    if (!centeredOnSelf && snapshotApplied && selfPlayerId !== null) {
      const self = model.players.get(selfPlayerId);
      if (self) {
        const iso = tileToIso(self.position[0], self.position[1]);
        camera.state.panX = iso.x;
        camera.state.panY = iso.y;
        camera.setFollowTarget(iso.x, iso.y);
        centeredOnSelf = true;
        phase0.info('camera_centered_on_self', {
          player_id: selfPlayerId,
          tile_x: self.position[0],
          tile_y: self.position[1],
        });
        debugLog('input', 'info', `camera_centered player=${selfPlayerId} tile=(${self.position[0]},${self.position[1]})`);
      }
    }

    if (centeredOnSelf && selfPlayerId !== null) {
      const self = model.players.get(selfPlayerId);
      if (self) {
        const iso = tileToIso(self.position[0], self.position[1]);
        camera.setFollowTarget(iso.x, iso.y);
      }
    }
    camera.update();
    camera.apply(scene.worldContainer, viewW, viewH);

    // Destination marker — sync navGoal from the model and pulse alpha.
    {
      const selfGoal =
        selfPlayerId !== null
          ? (model.players.get(selfPlayerId)?.navGoal ?? null)
          : null;
      updateNavMarker(selfGoal as [number, number] | null);
      if (navMarkerGraphics !== null) {
        const t = (now / 1000) * NAV_MARKER_PULSE_HZ * 2 * Math.PI;
        const mid = (NAV_MARKER_ALPHA_MIN + NAV_MARKER_ALPHA_MAX) / 2;
        const amp = (NAV_MARKER_ALPHA_MAX - NAV_MARKER_ALPHA_MIN) / 2;
        navMarkerGraphics.alpha = mid + amp * Math.sin(t);
      }
    }

    // Cutaway check per §4.5: only the building the local player is in
    // cuts away. `-1` sentinel = no cutaway active.
    let cutawayRoomId = -1;
    if (selfPlayerId !== null) {
      const self = model.players.get(selfPlayerId);
      if (self && model.width > 0) {
        const [px, py] = self.position;
        if (px >= 0 && px < model.width && py >= 0 && py < model.height) {
          const rid = model.tileRoomIds[py * model.width + px] ?? -1;
          if (rid !== undefined && rid >= 0) cutawayRoomId = rid;
        }
      }
    }

    // Cave z_level gating (M2.4)
    const localZLevel = selfPlayerId !== null
      ? (model.players.get(selfPlayerId)?.z_level ?? 0)
      : 0;

    // Toggle surface vs cave layers
    scene.layers.tileLayer.visible = localZLevel === 0;
    scene.layers.caveTileLayer.visible = localZLevel === -1;
    scene.layers.wallLayer.visible = localZLevel === 0;
    scene.layers.fogLayer.visible = localZLevel === 0;

    tiles.render(model, camera.state, viewW, viewH);
    cliffEdges.render(model, camera.state, viewW, viewH);
    walls.render(model, camera.state, viewW, viewH, cutawayRoomId);
    props.render(model, camera.state, viewW, viewH);
    avatars.render(model, camera.state, selfPlayerId, tickHz, now);
    fog.render(model, camera.state, selfPlayerId, viewW, viewH);
    caveTiles.render(model, camera.state, viewW, viewH, localZLevel);
    caveMouths.render(model, camera.state, viewW, viewH, localZLevel);
    devOverlay.render(model, camera.state, viewW, viewH);
    moisture.render(model, camera.state, viewW, viewH);

    // Day/night tint: cave darkness when underground, indoor palette if
    // in a room, otherwise outdoor tint from phase + day_tick (M2.1).
    tinter.apply(model.clock.phase, model.clock.day_tick, cutawayRoomId >= 0, localZLevel);

    // Weather is screen-space — pass current viewport dimensions directly.
    weather.render(model.weather, viewW, viewH);

    // Speech bubbles ride on the overlay (screen space) but compute
    // their screen position from the speaker's world-space tile each
    // frame so the bubble tracks the avatar as the camera pans.
    bubbles.render(model, camera.state, viewW, viewH, now);
  };
  scene.app.ticker.add(tickerFn);

  /** Brief diamond-outline flash on the click target — fades alpha
   *  over `MOVE_FLASH_MS` via the existing PixiJS ticker, then self-
   *  destroys. Lives on the world-space floor-overlay layer so it
   *  pans + zooms with the tile. */
  function flashTargetTile(s: SceneGraph, tileX: number, tileY: number): void {
    const halfW = TILE_WIDTH_PX / 2;
    const halfH = TILE_HEIGHT_PX / 2;
    const { x: ix, y: iy } = tileToIso(tileX, tileY);
    const g = new Graphics();
    g.poly([ix, iy - halfH, ix + halfW, iy, ix, iy + halfH, ix - halfW, iy]);
    g.stroke({ color: MOVE_FLASH_COLOR, width: 3, alpha: 0.95 });
    g.fill({ color: MOVE_FLASH_COLOR, alpha: 0.22 });
    s.layers.floorOverlayLayer.addChild(g);

    const startedAt = performance.now();
    const fade = (): void => {
      const t = (performance.now() - startedAt) / MOVE_FLASH_MS;
      if (t >= 1) {
        s.app.ticker.remove(fade);
        g.destroy();
        return;
      }
      g.alpha = 1 - t;
    };
    s.app.ticker.add(fade);
  }

  let destroyed = false;
  return {
    app: scene.app,
    scene,
    camera,
    destroy: () => {
      if (destroyed) return;
      destroyed = true;
      useContextMenu.getState().close();
      if (moveToDebounce !== null) window.clearTimeout(moveToDebounce);
      scene.app.canvas.removeEventListener('pointerdown', onPointerDown);
      scene.app.ticker.remove(tickerFn);
      if (navMarkerGraphics !== null) {
        navMarkerGraphics.destroy();
        navMarkerGraphics = null;
      }
      tiles.destroy();
      cliffEdges.destroy();
      walls.destroy();
      props.destroy();
      avatars.destroy();
      weather.destroy();
      tinter.destroy();
      bubbles.destroy();
      fog.destroy();
      caveTiles.destroy();
      caveMouths.destroy();
      devOverlay.destroy();
      moisture.destroy();
      camera.destroy();
      scene.destroy();
    },
  };
}

const WATER_MATERIAL = 6;
const WATER_CHECK_RADIUS = 2;

export function hasWaterNearby(model: WorldModel, cx: number, cy: number): boolean {
  for (let dy = -WATER_CHECK_RADIUS; dy <= WATER_CHECK_RADIUS; dy++) {
    for (let dx = -WATER_CHECK_RADIUS; dx <= WATER_CHECK_RADIUS; dx++) {
      const x = cx + dx;
      const y = cy + dy;
      if (x >= 0 && x < model.width && y >= 0 && y < model.height) {
        if (model.tileMaterials[y * model.width + x] === WATER_MATERIAL) {
          return true;
        }
      }
    }
  }
  return false;
}

function tileHasProp(model: WorldModel, x: number, y: number): boolean {
  for (const prop of model.props.values()) {
    if (prop.position[0] === x && prop.position[1] === y) return true;
  }
  return false;
}

// When the clicked tile has a prop, find the nearest cardinal neighbor
// without props so "Walk Here" targets a reachable tile instead of
// getting a "path blocked" rejection from the server.
function findWalkTarget(
  model: WorldModel,
  tileX: number,
  tileY: number,
  selfPos: [number, number] | null,
): [number, number] {
  if (!tileHasProp(model, tileX, tileY)) return [tileX, tileY];

  const offsets: [number, number][] = [[-1, 0], [1, 0], [0, -1], [0, 1]];
  const candidates: [number, number][] = [];
  for (const [dx, dy] of offsets) {
    const nx = tileX + dx;
    const ny = tileY + dy;
    if (nx >= 0 && nx < model.width && ny >= 0 && ny < model.height && !tileHasProp(model, nx, ny)) {
      candidates.push([nx, ny]);
    }
  }

  if (candidates.length === 0) return [tileX, tileY];
  if (!selfPos) return candidates[0]!;

  let best = candidates[0]!;
  let bestDist = Math.abs(best[0] - selfPos[0]) + Math.abs(best[1] - selfPos[1]);
  for (let i = 1; i < candidates.length; i++) {
    const c = candidates[i]!;
    const d = Math.abs(c[0] - selfPos[0]) + Math.abs(c[1] - selfPos[1]);
    if (d < bestDist) {
      best = c;
      bestDist = d;
    }
  }
  return best;
}
